import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Layers,
  Plus,
  SlidersHorizontal,
  BarChart4,
  Table as TableIcon,
  Image as ImageIcon,
  Monitor,
  LineChart,
  Phone,
  Mail,
  Globe,
  ArrowRight,
  CircleDot,
  Circle,
  ArrowDown,
} from "lucide-react";
import { motion } from "framer-motion";
import type {
  Slide,
  Theme,
} from "@/routes/~dashboard/~presentation/presentation-data";

interface SlidesPanelProps {
  slides: Slide[];
  currentIndex: number;
  onSelectSlide: (index: number) => void;
  onAddSlide: () => void;
  theme?: Theme;
}

export default function SlidesPanel({
  slides,
  currentIndex,
  onSelectSlide,
  onAddSlide,
  theme,
}: SlidesPanelProps) {
  const renderSlideThumbnail = (slide: Slide) => {
    // Title slide
    if (slide.layout === "titleSlide") {
      return (
        <div className="flex justify-center p-4">
          <div className="h-1 w-48 rounded-full bg-gray-600"></div>
        </div>
      );
    }

    // Contact slide
    if (slide.layout === "contactInfo") {
      return (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="w-28 rounded bg-gray-300 p-2">
            <div className="mb-2 flex items-center">
              <Phone className="mr-2 h-3 w-3 text-gray-500" />
              <div className="h-1 w-16 rounded-full bg-gray-400"></div>
            </div>
            <div className="mb-2 flex items-center">
              <Mail className="mr-2 h-3 w-3 text-gray-500" />
              <div className="h-1 w-16 rounded-full bg-gray-400"></div>
            </div>
            <div className="flex items-center">
              <Globe className="mr-2 h-3 w-3 text-gray-500" />
              <div className="h-1 w-16 rounded-full bg-gray-400"></div>
            </div>
          </div>
        </div>
      );
    }

    // Process horizontal
    if (slide.layout === "processHorizontal") {
      return (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="flex w-full items-center justify-between px-2">
            <div className="flex flex-col items-center">
              <Circle className="h-4 w-4 text-blue-500" />
              <div className="mt-1 h-1 w-8 rounded-full bg-gray-600"></div>
            </div>
            <ArrowRight className="h-3 w-3 text-gray-500" />
            <div className="flex flex-col items-center">
              <Circle className="h-4 w-4 text-green-500" />
              <div className="mt-1 h-1 w-8 rounded-full bg-gray-600"></div>
            </div>
            <ArrowRight className="h-3 w-3 text-gray-500" />
            <div className="flex flex-col items-center">
              <Circle className="h-4 w-4 text-amber-500" />
              <div className="mt-1 h-1 w-8 rounded-full bg-gray-600"></div>
            </div>
          </div>
        </div>
      );
    }

    // Process vertical
    if (slide.layout === "processVertical") {
      return (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="flex items-center">
              <Circle className="mr-2 h-4 w-4 text-blue-500" />
              <div className="h-1 w-12 rounded-full bg-gray-600"></div>
            </div>
            <ArrowDown className="h-3 w-3 text-gray-500" />
            <div className="flex items-center">
              <Circle className="mr-2 h-4 w-4 text-green-500" />
              <div className="h-1 w-12 rounded-full bg-gray-600"></div>
            </div>
            <ArrowDown className="h-3 w-3 text-gray-500" />
            <div className="flex items-center">
              <Circle className="mr-2 h-4 w-4 text-amber-500" />
              <div className="h-1 w-12 rounded-full bg-gray-600"></div>
            </div>
          </div>
        </div>
      );
    }

    // Timeline horizontal
    if (slide.layout === "timelineHorizontal") {
      return (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="relative w-full px-2">
            <div className="absolute top-4 h-0.5 w-full bg-gray-600"></div>
            <div className="flex w-full justify-between">
              <div className="flex flex-col items-center">
                <div className="mb-1 text-[6px] text-gray-400">2020</div>
                <CircleDot className="z-10 h-3 w-3 text-blue-500" />
                <div className="mt-1 h-1 w-8 rounded-full bg-gray-600"></div>
              </div>
              <div className="flex flex-col items-center">
                <div className="mb-1 text-[6px] text-gray-400">2021</div>
                <CircleDot className="z-10 h-3 w-3 text-green-500" />
                <div className="mt-1 h-1 w-8 rounded-full bg-gray-600"></div>
              </div>
              <div className="flex flex-col items-center">
                <div className="mb-1 text-[6px] text-gray-400">2022</div>
                <CircleDot className="z-10 h-3 w-3 text-amber-500" />
                <div className="mt-1 h-1 w-8 rounded-full bg-gray-600"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Timeline vertical
    if (slide.layout === "timelineVertical") {
      return (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="relative flex h-full justify-center">
            <div className="absolute left-4 h-full w-0.5 bg-gray-600"></div>
            <div className="flex h-full flex-col justify-between py-1 pl-6">
              <div className="flex items-center">
                <CircleDot className="absolute left-4 z-10 h-3 w-3 -translate-x-1.5 transform text-blue-500" />
                <div className="ml-2">
                  <div className="text-[6px] text-gray-400">2020</div>
                  <div className="mt-0.5 h-1 w-8 rounded-full bg-gray-600"></div>
                </div>
              </div>
              <div className="flex items-center">
                <CircleDot className="absolute left-4 z-10 h-3 w-3 -translate-x-1.5 transform text-green-500" />
                <div className="ml-2">
                  <div className="text-[6px] text-gray-400">2021</div>
                  <div className="mt-0.5 h-1 w-8 rounded-full bg-gray-600"></div>
                </div>
              </div>
              <div className="flex items-center">
                <CircleDot className="absolute left-4 z-10 h-3 w-3 -translate-x-1.5 transform text-amber-500" />
                <div className="ml-2">
                  <div className="text-[6px] text-gray-400">2022</div>
                  <div className="mt-0.5 h-1 w-8 rounded-full bg-gray-600"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Chart layouts
    if (slide.layout === "chartSingle") {
      return (
        <div className="flex flex-1 flex-col items-center justify-center">
          <BarChart4 className="h-20 w-20 rounded-sm bg-gray-300 p-4 text-gray-500" />
        </div>
      );
    }

    if (slide.layout === "chartDual") {
      return (
        <div className="flex flex-1 items-center justify-center gap-2 space-x-6">
          <div className="flex flex-col items-center rounded-sm">
            <BarChart4 className="h-16 w-16 rounded-sm bg-gray-300 p-4 text-gray-500" />
          </div>
          <div className="flex flex-col items-center">
            <LineChart className="h-16 w-16 rounded-sm bg-gray-300 p-4 text-gray-500" />
          </div>
        </div>
      );
    }

    if (slide.layout === "dashboard") {
      return (
        <div className="grid flex-1 grid-cols-2 gap-2 p-1">
          <div className="flex items-center justify-center rounded-sm bg-gray-300">
            <BarChart4 className="h-6 w-6 text-gray-500" />
          </div>
          <div className="flex items-center justify-center rounded-sm bg-gray-300">
            <LineChart className="h-6 w-6 text-gray-500" />
          </div>
          <div className="flex items-center justify-center rounded-sm bg-gray-300">
            <TableIcon className="h-6 w-6 text-gray-500" />
          </div>
          <div className="flex items-center justify-center rounded-sm bg-gray-300">
            <BarChart4 className="h-6 w-6 text-gray-500" />
          </div>
        </div>
      );
    }

    // Table layout
    if (slide.layout === "table" && slide.table) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="h-20 w-32 overflow-hidden rounded border border-gray-400 bg-gray-300">
            <div className="border-b border-gray-700 bg-gray-600 p-1">
              <div className="h-1 w-12 rounded-full bg-gray-200"></div>
            </div>
            <div className="flex flex-col">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex border-b border-gray-400">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="w-8 border-r border-gray-300 p-1">
                      <div className="h-1 w-4 rounded-full bg-gray-500"></div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Image with caption
    if (slide.layout === "imageWithCaption") {
      return (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="mb-2 flex h-16 w-24 items-center justify-center rounded bg-gray-300">
              <ImageIcon className="h-8 w-8 text-gray-500" />
            </div>
            <div className="h-1 w-16 rounded-full bg-gray-400"></div>
            <div className="mt-1 h-1 w-20 rounded-full bg-gray-400"></div>
          </div>
        </div>
      );
    }

    // Video with caption
    if (slide.layout === "videoWithCaption") {
      return (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="mb-2 flex h-16 w-24 items-center justify-center rounded bg-gray-300">
              <Monitor className="h-8 w-8 text-gray-500" />
            </div>
            <div className="h-1 w-16 rounded-full bg-gray-400"></div>
            <div className="mt-1 h-1 w-20 rounded-full bg-gray-400"></div>
          </div>
        </div>
      );
    }

    // Image grid
    if (slide.layout === "imageGrid") {
      return (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-300">
              <ImageIcon className="h-6 w-6 text-gray-500" />
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-300">
              <ImageIcon className="h-6 w-6 text-gray-500" />
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-300">
              <ImageIcon className="h-6 w-6 text-gray-500" />
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-300">
              <ImageIcon className="h-6 w-6 text-gray-500" />
            </div>
          </div>
        </div>
      );
    }

    // Content with media on the right
    if (slide.layout === "contentWithMediaRight") {
      return (
        <div className="flex flex-1 items-center justify-between p-1">
          <div className="flex flex-col space-y-1 pr-2">
            <div className="h-1 w-12 rounded-full bg-gray-400"></div>
            <div className="h-1 w-14 rounded-full bg-gray-400"></div>
            <div className="h-1 w-10 rounded-full bg-gray-400"></div>
          </div>
          <div className="flex h-16 w-16 items-center justify-center rounded bg-gray-300">
            <ImageIcon className="h-8 w-8 text-gray-500" />
          </div>
        </div>
      );
    }

    // Content with media on the left
    if (slide.layout === "contentWithMediaLeft") {
      return (
        <div className="flex flex-1 items-center justify-between p-1">
          <div className="flex h-16 w-16 items-center justify-center rounded bg-gray-300">
            <ImageIcon className="h-8 w-8 text-gray-500" />
          </div>
          <div className="flex flex-col space-y-1 pl-2">
            <div className="h-1 w-12 rounded-full bg-gray-400"></div>
            <div className="h-1 w-14 rounded-full bg-gray-400"></div>
            <div className="h-1 w-10 rounded-full bg-gray-400"></div>
          </div>
        </div>
      );
    }

    // Any slide with media or image
    if (slide.media || slide.image) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="flex h-20 w-24 items-center justify-center rounded bg-gray-700">
            <ImageIcon className="h-10 w-10 text-gray-500" />
          </div>
        </div>
      );
    }

    // Two column layout
    if (slide.layout === "contentTwoCol") {
      return (
        <div className="flex flex-1 items-center justify-between space-x-2 p-1">
          <div className="flex flex-1 flex-col space-y-1">
            <div className="h-1 w-full rounded-full bg-gray-600"></div>
            <div className="h-1 w-full rounded-full bg-gray-600"></div>
            <div className="h-1 w-2/3 rounded-full bg-gray-600"></div>
          </div>
          <div className="mx-1 h-16 border-r border-gray-600"></div>
          <div className="flex flex-1 flex-col space-y-1">
            <div className="h-1 w-full rounded-full bg-gray-600"></div>
            <div className="h-1 w-full rounded-full bg-gray-600"></div>
            <div className="h-1 w-3/4 rounded-full bg-gray-600"></div>
          </div>
        </div>
      );
    }

    // Multiple column layout
    if (slide.layout === "contentFourCol") {
      return (
        <div className="flex flex-1 items-center justify-between p-1">
          <div className="grid w-full grid-cols-4 gap-2">
            <div className="flex flex-col space-y-1">
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-2/3 rounded-full bg-gray-600"></div>
            </div>
            <div className="flex flex-col space-y-1">
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-2/3 rounded-full bg-gray-600"></div>
            </div>
            <div className="flex flex-col space-y-1">
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-2/3 rounded-full bg-gray-600"></div>
            </div>
            <div className="flex flex-col space-y-1">
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-2/3 rounded-full bg-gray-600"></div>
            </div>
          </div>
        </div>
      );
    }

    if (slide.layout === "contentThreeCol") {
      return (
        <div className="flex flex-1 items-center justify-between p-1">
          <div className="grid w-full grid-cols-3 gap-2">
            <div className="flex flex-col space-y-1">
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-2/3 rounded-full bg-gray-600"></div>
            </div>
            <div className="flex flex-col space-y-1">
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-2/3 rounded-full bg-gray-600"></div>
            </div>
            <div className="flex flex-col space-y-1">
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-full rounded-full bg-gray-600"></div>
              <div className="h-1 w-2/3 rounded-full bg-gray-600"></div>
            </div>
          </div>
        </div>
      );
    }

    // Default text content display
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-2">
        <div className="flex flex-col items-center space-y-2">
          <div className="h-1 w-56 rounded-full bg-gray-600"></div>
          <div className="h-1 w-52 rounded-full bg-gray-600"></div>
          <div className="h-1 w-56 rounded-full bg-gray-600"></div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className="bg-primary/50 border-b border-secondary p-4">
        <Button
          variant="outline"
          className="w-full border-secondary bg-secondary text-secondary-foreground hover:bg-accent"
          onClick={onAddSlide}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add New Slide
        </Button>
      </div>
      <div className="flex items-center justify-between p-4 text-xs text-secondary-foreground">
        <div className="flex items-center gap-1">
          <Layers className="h-3 w-3" />
          <span>{slides.length} slides</span>
        </div>
        <div className="flex items-center gap-1">
          <SlidersHorizontal className="h-3 w-3" />
          <span>Sorted by order</span>
        </div>
      </div>
      <ScrollArea className="flex-1 overflow-hidden">
        <div className="flex flex-col items-center justify-center space-y-3 p-2">
          {slides.map((slide, index) => (
            <motion.div
              key={slide.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: index * 0.05 }}
              className={`w-full cursor-pointer rounded-lg p-3 transition-all hover:translate-y-[-2px] ${
                index === currentIndex
                  ? "border-accent/40 border-2 bg-secondary shadow-[0_0_10px_rgba(59,130,246,0.1)]"
                  : "border-2 border-secondary bg-slate-900 hover:bg-secondary"
              }`}
              onClick={() => onSelectSlide(index)}
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="rounded-sm bg-cyan-100 px-2 py-0.5 text-xs font-medium text-black">
                  Slide {index + 1}
                </div>
                <div className="rounded-sm bg-amber-100 px-2 py-0.5 text-xs font-medium text-black">
                  {slide.layout}
                </div>
              </div>
              <div
                className="aspect-[16/9] h-36 w-full overflow-hidden rounded-md"
                style={{
                  backgroundColor:
                    slide.background ||
                    (theme ? theme.slideBackground : "white"),
                }}
              >
                <div className="flex h-full w-full flex-col p-3">
                  <div
                    className="truncate text-xs font-medium"
                    style={{
                      color:
                        slide.titleColor ||
                        (theme ? theme.titleColor : "#000000"),
                    }}
                  >
                    {slide.title || "Untitled Slide"}
                  </div>
                  {renderSlideThumbnail(slide)}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
